/**
 * =============================================================================
 * App Team Detail Page
 * =============================================================================
 *
 * View and manage app team details including:
 * - Team information
 * - Harbor credentials
 * - Team members (from Keycloak)
 * - Apps belonging to team
 *
 * Access:
 * - system_admin, app_admin: Full access
 * - app_dev: View only (their teams)
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  appTeamsApi,
  appsApi,
  localUsersApi,
  AppTeam,
  AppTeamUpdate,
  AppTeamMember,
  AppListItem,
  AppTeamMemberCreate,
  UnifiedUser,
} from '../services/api'
import Modal from '../components/Modal'
import DataTable, { Column } from '../components/DataTable'
import { useAuth } from '../auth/AuthProvider'

// =============================================================================
// Edit Team Modal
// =============================================================================

interface EditTeamModalProps {
  isOpen: boolean
  onClose: () => void
  team: AppTeam
}

const EditTeamModal: React.FC<EditTeamModalProps> = ({ isOpen, onClose, team }) => {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<AppTeamUpdate>({
    name: team.name,
    team_email: team.team_email || '',
    app_limit: team.app_limit,
  })
  const [error, setError] = useState<string | null>(null)

  const updateMutation = useMutation({
    mutationFn: (data: AppTeamUpdate) => appTeamsApi.update(team.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-team', team.id] })
      queryClient.invalidateQueries({ queryKey: ['app-teams'] })
      onClose()
    },
    onError: (err: unknown) => {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Failed to update team')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateMutation.mutate(formData)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Team">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Team Name
          </label>
          <input
            type="text"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Team Email
          </label>
          <input
            type="email"
            value={formData.team_email || ''}
            onChange={(e) => setFormData({ ...formData, team_email: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            App Limit
          </label>
          <input
            type="number"
            min={1}
            max={100}
            value={formData.app_limit}
            onChange={(e) => setFormData({ ...formData, app_limit: parseInt(e.target.value) || 5 })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// =============================================================================
// Add Member Modal
// =============================================================================

interface AddMemberModalProps {
  isOpen: boolean
  users: UnifiedUser[]
  loading: boolean
  saving: boolean
  onClose: () => void
  onSubmit: (payload: AppTeamMemberCreate) => void
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  users,
  loading,
  saving,
  onClose,
  onSubmit,
}) => {
  const [selectedUserId, setSelectedUserId] = useState('')

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Team Member">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Local User</label>
          <select
            className="input"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            disabled={loading || saving}
          >
            <option value="">Select user</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username} ({user.email || 'no email'})
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              if (!selectedUserId) return
              onSubmit({ member_source: 'local', local_user_id: selectedUserId })
            }}
            disabled={saving || loading || !selectedUserId}
          >
            {saving ? 'Adding...' : 'Add Member'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// =============================================================================
// Credentials Section
// =============================================================================

interface CredentialsSectionProps {
  teamId: string
  canView: boolean
}

const CredentialsSection: React.FC<CredentialsSectionProps> = ({ teamId, canView }) => {
  const [showCredentials, setShowCredentials] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  const { data: credentials, isLoading, error, refetch } = useQuery({
    queryKey: ['app-team-credentials', teamId],
    queryFn: () => appTeamsApi.getCredentials(teamId),
    enabled: showCredentials && canView,
  })

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopied(field)
    setTimeout(() => setCopied(null), 2000)
  }

  if (!canView) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-gray-500">
        You don't have permission to view Harbor credentials.
      </div>
    )
  }

  const errorDetail = (error as any)?.response?.data?.detail
  const errorMessage = typeof errorDetail === 'string' ? errorDetail : 'Failed to load credentials.'

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Harbor Credentials</h3>
        <button
          onClick={() => {
            if (!showCredentials) {
              refetch()
            }
            setShowCredentials(!showCredentials)
          }}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {showCredentials ? 'Hide' : 'Show'} Credentials
        </button>
      </div>

      {showCredentials && (
        <>
          {isLoading ? (
            <div className="text-gray-500">Loading credentials...</div>
          ) : error ? (
            <div className="text-red-600 text-sm">
              {errorMessage}
            </div>
          ) : credentials?.data ? (
            <div className="space-y-3">
              {/* Full Registry URI - most important for developers */}
              <div className="bg-blue-50 rounded-lg p-4 mb-2">
                <label className="block text-sm font-medium text-blue-800 mb-2">Full Registry Path (for docker push/pull)</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-blue-200 px-3 py-2 rounded text-sm font-mono break-all text-blue-900">
                    {credentials.data.registry_url.replace('https://', '')}/{credentials.data.project_name}/
                  </code>
                  <button
                    onClick={() => copyToClipboard(`${credentials.data.registry_url.replace('https://', '')}/${credentials.data.project_name}/`, 'fullpath')}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm whitespace-nowrap"
                  >
                    {copied === 'fullpath' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Example: {credentials.data.registry_url.replace('https://', '')}/{credentials.data.project_name}/my-app@sha256:abc123...
                </p>
              </div>

              <CredentialRow
                label="Registry URL"
                value={credentials.data.registry_url}
                copied={copied === 'registry'}
                onCopy={() => copyToClipboard(credentials.data.registry_url, 'registry')}
              />
              <CredentialRow
                label="Project Name"
                value={credentials.data.project_name}
                copied={copied === 'project'}
                onCopy={() => copyToClipboard(credentials.data.project_name, 'project')}
              />
              <CredentialRow
                label="Robot Account"
                value={credentials.data.robot_account}
                copied={copied === 'robot'}
                onCopy={() => copyToClipboard(credentials.data.robot_account, 'robot')}
              />
              <CredentialRow
                label="Robot Secret"
                value={credentials.data.robot_secret}
                copied={copied === 'secret'}
                onCopy={() => copyToClipboard(credentials.data.robot_secret, 'secret')}
                isSecret
              />

              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Docker Login Command:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-800 text-green-400 px-3 py-2 rounded text-sm font-mono overflow-x-auto">
                    docker login {credentials.data.registry_url.replace('https://', '')} -u {credentials.data.robot_account}
                  </code>
                  <button
                    onClick={() => copyToClipboard(`docker login ${credentials.data.registry_url.replace('https://', '')} -u ${credentials.data.robot_account}`, 'docker')}
                    className="px-2 py-2 text-gray-400 hover:text-white"
                  >
                    {copied === 'docker' ? '✓' : '📋'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-red-500">Failed to load credentials</div>
          )}
        </>
      )}
    </div>
  )
}

interface CredentialRowProps {
  label: string
  value: string
  copied: boolean
  onCopy: () => void
  isSecret?: boolean
}

const CredentialRow: React.FC<CredentialRowProps> = ({ label, value, copied, onCopy, isSecret }) => {
  const [showSecret, setShowSecret] = useState(false)

  return (
    <div>
      <label className="block text-sm font-medium text-gray-500 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all">
          {isSecret && !showSecret ? '••••••••••••••••••••' : value}
        </code>
        {isSecret && (
          <button
            onClick={() => setShowSecret(!showSecret)}
            className="px-2 py-2 text-gray-500 hover:text-gray-700"
          >
            {showSecret ? '🙈' : '👁'}
          </button>
        )}
        <button
          onClick={onCopy}
          className="px-2 py-2 text-gray-500 hover:text-gray-700"
        >
          {copied ? '✓' : '📋'}
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

const AppTeamDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddMemberModal, setShowAddMemberModal] = useState(false)

  const isAdmin = user?.roles?.includes('system_admin') || user?.roles?.includes('app_admin')

  // Fetch team details
  const { data: team, isLoading: teamLoading } = useQuery({
    queryKey: ['app-team', id],
    queryFn: () => appTeamsApi.get(id!),
    enabled: !!id,
  })
  const isLocalTeam = team?.data?.keycloak_group_id?.startsWith('local-') ?? false

  // Fetch team members
  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['app-team-members', id],
    queryFn: () => appTeamsApi.getMembers(id!),
    enabled: !!id,
  })

  const { data: localUsers, isLoading: localUsersLoading } = useQuery({
    queryKey: ['local-users', 'app-team-add'],
    queryFn: () => localUsersApi.listCombined({ auth_provider: 'local' }),
    enabled: isAdmin && isLocalTeam,
  })

  // Fetch team apps
  const { data: apps, isLoading: appsLoading } = useQuery({
    queryKey: ['app-team-apps', id],
    queryFn: () => appsApi.list({ app_team_id: id }),
    enabled: !!id,
  })

  // Status toggle mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async () => {
      if (team?.data.status === 'active') {
        return appTeamsApi.deactivate(id!)
      } else {
        return appTeamsApi.activate(id!)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-team', id] })
      queryClient.invalidateQueries({ queryKey: ['app-teams'] })
    },
  })

  const addMemberMutation = useMutation({
    mutationFn: (payload: AppTeamMemberCreate) => appTeamsApi.addMember(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-team-members', id] })
      setShowAddMemberModal(false)
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: (memberId: string) => appTeamsApi.removeMember(id!, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-team-members', id] })
    },
  })

  // Member columns
  const memberColumns: Column<AppTeamMember>[] = [
    {
      key: 'username',
      header: 'Username',
      render: (member) => <span className="font-medium">{member.username || member.display_name || '-'}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      render: (member) => `${member.first_name || ''} ${member.last_name || ''}`.trim() || '-',
    },
    {
      key: 'email',
      header: 'Email',
      render: (member) => member.email || '-',
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (member) => (member.roles && member.roles.length ? member.roles.join(', ') : '-'),
    },
    ...(isAdmin && isLocalTeam ? [{
      key: 'actions',
      header: 'Actions',
      render: (member: AppTeamMember) => (
        <button
          className="text-red-600 hover:text-red-800 text-sm"
          onClick={() => {
            if (member.id && confirm('Remove this member from the team?')) {
              removeMemberMutation.mutate(member.id)
            }
          }}
        >
          Remove
        </button>
      ),
    }] : []),
  ]

  // App columns
  const appColumns: Column<AppListItem>[] = [
    {
      key: 'name',
      header: 'App',
      render: (app) => (
        <div>
          <Link to={`/apps/${app.id}`} className="font-medium text-blue-600 hover:text-blue-800">
            {app.name}
          </Link>
          <div className="text-xs text-gray-500">{app.app_id} {(app as any).current_version ? `v${(app as any).current_version}` : ''}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (app) => {
        const status = app.status || 'draft'
        const statusColors: Record<string, string> = {
          draft: 'bg-gray-100 text-gray-800',
          pending_approval: 'bg-yellow-100 text-yellow-800',
          approved: 'bg-green-100 text-green-800',
          rejected: 'bg-red-100 text-red-800',
          deprecated: 'bg-orange-100 text-orange-800',
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
            {status.replace('_', ' ')}
          </span>
        )
      },
    },
    {
      key: 'environment',
      header: 'Env',
      render: (app) => (
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
          app.environment === 'prod' ? 'bg-purple-100 text-purple-800' :
          app.environment === 'test' ? 'bg-blue-100 text-blue-800' :
          'bg-gray-100 text-gray-600'
        }`}>
          {(app.environment || 'dev').toUpperCase()}
        </span>
      ),
    },
    {
      key: 'developer',
      header: 'Developer',
      render: (app) => app.developer_name || '-',
    },
  ]

  if (teamLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading team details...</div>
      </div>
    )
  }

  if (!team?.data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Team not found</div>
      </div>
    )
  }

  const teamData = team.data

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/app-teams')}
            className="text-blue-600 hover:text-blue-800 text-sm mb-2 flex items-center gap-1"
          >
            ← Back to App Teams
          </button>
          <h2 className="text-2xl font-semibold text-gray-900">{teamData.name}</h2>
          <p className="text-sm text-gray-500">{teamData.keycloak_group_path}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                onClick={() => toggleStatusMutation.mutate()}
                disabled={toggleStatusMutation.isPending}
                className={`px-4 py-2 rounded-md ${
                  teamData.status === 'active'
                    ? 'bg-orange-600 text-white hover:bg-orange-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                } disabled:opacity-50`}
              >
                {teamData.status === 'active' ? 'Deactivate' : 'Activate'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Team Info */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Team Information</h3>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                teamData.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {teamData.status}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Contact Email</dt>
            <dd className="mt-1 text-gray-900">{teamData.team_email || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Harbor Project</dt>
            <dd className="mt-1">
              <code className="bg-gray-100 px-2 py-1 rounded text-sm">{teamData.harbor_project_name}</code>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">App Limit</dt>
            <dd className="mt-1 text-gray-900">{teamData.app_count} / {teamData.app_limit} apps</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Members</dt>
            <dd className="mt-1 text-gray-900">{teamData.member_count} members</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Created</dt>
            <dd className="mt-1 text-gray-900">
              {teamData.created_at ? new Date(teamData.created_at).toLocaleDateString() : '-'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Harbor Credentials */}
      {isLocalTeam ? (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Harbor Credentials</h3>
          <p className="text-sm text-gray-600">
            Local-only teams do not have Harbor credentials stored. Use the credentials shown at creation.
          </p>
        </div>
      ) : (
        <CredentialsSection teamId={id!} canView={true} />
      )}

      {/* Team Members */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
          {isAdmin && isLocalTeam && (
            <button className="btn-secondary text-sm" onClick={() => setShowAddMemberModal(true)}>
              Add Member
            </button>
          )}
          {isAdmin && !isLocalTeam && (
            <p className="text-sm text-gray-500">
              Manage members in{' '}
              <a
                href="#"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Keycloak Admin Console
              </a>
            </p>
          )}
        </div>
        <DataTable
          columns={memberColumns}
          data={members?.data || []}
          loading={membersLoading}
          getRowKey={(member) => member.id ?? member.user_id}
          emptyMessage="No members in this team"
        />
      </div>

      {/* Team Apps */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Apps</h3>
          {teamData.app_count < teamData.app_limit && (
            <Link
              to="/apps/new"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              Create App
            </Link>
          )}
        </div>
        <DataTable
          columns={appColumns}
          data={apps?.data || []}
          loading={appsLoading}
          getRowKey={(app) => app.id}
          emptyMessage="No apps in this team yet"
        />
      </div>

      {/* Edit Modal */}
      {showEditModal && team?.data && (
        <EditTeamModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          team={team.data}
        />
      )}

      {showAddMemberModal && (
        <AddMemberModal
          isOpen={showAddMemberModal}
          users={localUsers?.data || []}
          loading={localUsersLoading}
          saving={addMemberMutation.isPending}
          onClose={() => setShowAddMemberModal(false)}
          onSubmit={(payload) => addMemberMutation.mutate(payload)}
        />
      )}
    </div>
  )
}

export default AppTeamDetailPage
